// Zero-dependency i18n for the widget. The console is fully bilingual (en/ar, RTL); this gives the
// embeddable widget the same built-in copy so Arabic end users don't get English. Strings can still be
// overridden per-field via LauncherConfig; this only changes the *defaults* selected by `locale`.

export type SnapLocale = 'en' | 'ar';
export type SnapDir = 'ltr' | 'rtl' | 'auto';

export interface SnapMessages {
  launcher: {
    title: string;
    subtitle: string;
    errorLabel: string;
    errorDescription: string;
    supportLabel: string;
    supportDescription: string;
  };
  report: {
    title: string;
    notesPlaceholder: string;
    capture: string;
    retake: string;
    cancel: string;
    submit: string;
  };
  support: {
    title: string;
    titlePlaceholder: string;
    descPlaceholder: string;
    capture: string;
    cancel: string;
    submit: string;
    done: string;
  };
  toast: {
    submitted: string;
    couldNotSubmit: string;
    networkError: string;
    savedOffline: string;
  };
}

const EN: SnapMessages = {
  launcher: {
    title: 'How can we help?',
    subtitle: 'Help & support',
    errorLabel: 'Report a problem',
    errorDescription: 'Capture this screen and send a bug report',
    supportLabel: 'Get support',
    supportDescription: "Open a ticket and we'll help you fix it",
  },
  report: {
    title: 'Report a problem',
    notesPlaceholder: 'Add any steps to reproduce or extra notes…',
    capture: 'Capture screen',
    retake: 'Retake screenshot',
    cancel: 'Cancel',
    submit: 'Submit report',
  },
  support: {
    title: 'Get support',
    titlePlaceholder: 'Short, descriptive title',
    descPlaceholder: 'Describe the issue…',
    capture: 'Capture screen',
    cancel: 'Cancel',
    submit: 'Submit',
    done: 'Done',
  },
  toast: {
    submitted: 'Ticket submitted',
    couldNotSubmit: 'Could not submit ticket',
    networkError: 'Network error',
    savedOffline: 'Saved offline (will retry)',
  },
};

const AR: SnapMessages = {
  launcher: {
    title: 'كيف يمكننا المساعدة؟',
    subtitle: 'المساعدة والدعم',
    errorLabel: 'الإبلاغ عن مشكلة',
    errorDescription: 'التقاط هذه الشاشة وإرسال تقرير عن الخطأ',
    supportLabel: 'الحصول على الدعم',
    supportDescription: 'افتح تذكرة وسنساعدك في حلها',
  },
  report: {
    title: 'الإبلاغ عن مشكلة',
    notesPlaceholder: 'أضف خطوات إعادة الإنتاج أو أي ملاحظات إضافية…',
    capture: 'التقاط الشاشة',
    retake: 'إعادة التقاط الصورة',
    cancel: 'إلغاء',
    submit: 'إرسال التقرير',
  },
  support: {
    title: 'الحصول على الدعم',
    titlePlaceholder: 'عنوان قصير ووصفي',
    descPlaceholder: 'صف المشكلة…',
    capture: 'التقاط الشاشة',
    cancel: 'إلغاء',
    submit: 'إرسال',
    done: 'تم',
  },
  toast: {
    submitted: 'تم إرسال التذكرة',
    couldNotSubmit: 'تعذّر إرسال التذكرة',
    networkError: 'خطأ في الشبكة',
    savedOffline: 'تم الحفظ دون اتصال (ستتم إعادة المحاولة)',
  },
};

const TABLES: Record<SnapLocale, SnapMessages> = { en: EN, ar: AR };

/** Resolve the locale: explicit config wins, else the host language (navigator.language), else 'en'. */
export function resolveLocale(explicit?: SnapLocale): SnapLocale {
  if (explicit === 'en' || explicit === 'ar') return explicit;
  if (typeof navigator !== 'undefined') {
    const lang = (navigator.language || '').toLowerCase();
    if (lang.startsWith('ar')) return 'ar';
  }
  return 'en';
}

export function getMessages(explicit?: SnapLocale): SnapMessages {
  return TABLES[resolveLocale(explicit)];
}

/** Resolve text direction: explicit 'ltr'/'rtl' wins; 'auto' (or unset) derives from the locale. */
export function resolveDir(dir: SnapDir | undefined, locale: SnapLocale): 'ltr' | 'rtl' {
  if (dir === 'ltr' || dir === 'rtl') return dir;
  return locale === 'ar' ? 'rtl' : 'ltr';
}
